import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiMailLine, RiLockLine, RiEyeLine, RiEyeOffLine,
  RiArrowLeftLine, RiCheckboxCircleFill, RiShieldKeyholeLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import { useAuth } from '../../context/AuthContext';
import { forgotPassword, verifyResetOtp, resetPassword } from '../../api/auth.api';
import KitsuMark from '../../components/brand/KitsuMark';

const OTP_LENGTH = 5;
const RESEND_SECONDS = 45;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { setAuthError } = useAuth();

  const [step, setStep] = useState('email'); // email → otp → password → done
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'otp') otpRefs.current[0]?.focus();
  }, [step]);

  const otp = digits.join('');

  /* ── step 1: request a code ── */
  const requestCode = async (event, { resend = false } = {}) => {
    event?.preventDefault();
    if (!email.trim()) { setError('Enter your email address.'); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await forgotPassword({ email: email.trim() });
      setStep('otp');
      setDigits(Array(OTP_LENGTH).fill(''));
      setCooldown(RESEND_SECONDS);
      setNotice(resend ? 'A new code is on its way.' : res.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ── step 2: verify the code ── */
  const verifyCode = async (event, codeOverride) => {
    event?.preventDefault();
    const code = codeOverride ?? otp;
    if (code.length !== OTP_LENGTH) { setError(`Enter all ${OTP_LENGTH} digits.`); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await verifyResetOtp({ email: email.trim(), otp: code });
      setResetToken(res.resetToken);
      setStep('password');
    } catch (err) {
      setError(err.message);
      setDigits(Array(OTP_LENGTH).fill(''));
      otpRefs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  /* ── step 3: set the new password ── */
  const submitPassword = async (event) => {
    event?.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Those passwords don’t match.'); return; }
    setBusy(true); setError('');
    try {
      await resetPassword({ token: resetToken, password });
      setStep('done');
      // The reset endpoint returns a fresh session token, but sending the user
      // through a normal sign-in keeps AuthContext the single source of truth.
      setAuthError('');
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ── OTP box handlers ── */
  const setDigit = (i, value) => {
    const clean = value.replace(/\D/g, '');
    if (!clean) { setDigits(d => d.map((x, idx) => (idx === i ? '' : x))); return; }
    // typing/pasting more than one digit fans out across the boxes
    setDigits(prev => {
      const next = [...prev];
      clean.split('').forEach((ch, offset) => {
        if (i + offset < OTP_LENGTH) next[i + offset] = ch;
      });
      return next;
    });
    const landed = Math.min(i + clean.length, OTP_LENGTH - 1);
    otpRefs.current[landed]?.focus();
  };

  const onOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) otpRefs.current[i + 1]?.focus();
  };

  const onOtpPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    text.split('').forEach((ch, idx) => { next[idx] = ch; });
    setDigits(next);
    otpRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
    if (text.length === OTP_LENGTH) verifyCode(null, text);
  };

  const heading = {
    email:    { title: 'Forgot your password?', sub: "Enter your email and we'll send you a reset code." },
    otp:      { title: 'Check your inbox',      sub: `We sent a ${OTP_LENGTH}-digit code to ${email}` },
    password: { title: 'Set a new password',    sub: 'Choose something you haven’t used before.' },
    done:     { title: 'Password updated',      sub: 'You can sign in with your new password now.' },
  }[step];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(249,115,22,0.06), transparent 70%)', pointerEvents: 'none' }} />

      <div className="ks-card anim-pop" style={{ padding: '40px 36px', width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <div style={{ fontSize: 36, marginBottom: 10, color: C.accent, display: 'flex', justifyContent: 'center' }}>
            {step === 'done' ? <RiCheckboxCircleFill style={{ color: C.green }} /> : <KitsuMark size={44} animation="pop" />}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.7, marginBottom: 5 }}>{heading.title}</h1>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}>{heading.sub}</p>
        </div>

        {notice && step === 'otp' && (
          <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.28)', borderRadius: 9, padding: '10px 14px', fontSize: 12.5, color: C.green, fontWeight: 600, marginBottom: 14, lineHeight: 1.5 }}>
            {notice}
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.28)', borderRadius: 9, padding: '10px 14px', fontSize: 13, color: C.red, fontWeight: 600, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* ── Step 1 · email ── */}
        {step === 'email' && (
          <form onSubmit={requestCode} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ position: 'relative' }}>
              <RiMailLine size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
              <input
                className="ks-input" style={{ paddingLeft: 38 }} type="email" autoFocus
                placeholder="Email address" value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={busy}
              style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15, marginTop: 4 }}>
              {busy ? <div className="spinner" /> : 'Send reset code'}
            </button>
          </form>
        )}

        {/* ── Step 2 · OTP ── */}
        {step === 'otp' && (
          <form onSubmit={verifyCode} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }} onPaste={onOtpPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  className="otp-box"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={OTP_LENGTH}
                  value={d}
                  onChange={e => setDigit(i, e.target.value)}
                  onKeyDown={e => onOtpKeyDown(i, e)}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>

            <button type="submit" className="btn-primary" disabled={busy || otp.length !== OTP_LENGTH}
              style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15 }}>
              {busy ? <div className="spinner" /> : 'Verify code'}
            </button>

            <div style={{ textAlign: 'center', fontSize: 12.5, color: C.muted }}>
              Didn&apos;t get it?{' '}
              {cooldown > 0 ? (
                <span style={{ color: C.faint }}>Resend in {cooldown}s</span>
              ) : (
                <span style={{ color: C.accent, cursor: 'pointer', fontWeight: 700 }}
                  onClick={e => requestCode(e, { resend: true })}>Resend code</span>
              )}
            </div>
          </form>
        )}

        {/* ── Step 3 · new password ── */}
        {step === 'password' && (
          <form onSubmit={submitPassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ position: 'relative' }}>
              <RiLockLine size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
              <input
                className="ks-input" style={{ paddingLeft: 38, paddingRight: 42 }} autoFocus
                placeholder="New password" type={showPass ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)}
              />
              <div onClick={() => setShowPass(s => !s)}
                style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted, cursor: 'pointer' }}>
                {showPass ? <RiEyeOffLine size={15} /> : <RiEyeLine size={15} />}
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <RiLockLine size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: C.muted }} />
              <input
                className="ks-input" style={{ paddingLeft: 38 }}
                placeholder="Confirm new password" type={showPass ? 'text' : 'password'}
                value={confirm} onChange={e => setConfirm(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={busy}
              style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15, marginTop: 4 }}>
              {busy ? <div className="spinner" /> : 'Update password'}
            </button>
          </form>
        )}

        {/* ── Step 4 · done ── */}
        {step === 'done' && (
          <button className="btn-primary" onClick={() => navigate('/login')}
            style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15 }}>
            Go to sign in
          </button>
        )}

        {step !== 'done' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, color: C.faint, fontSize: 12, lineHeight: 1.5 }}>
            <RiShieldKeyholeLine size={14} style={{ flexShrink: 0 }} />
            Codes expire after 10 minutes and can only be used once.
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <span style={{ color: C.muted, fontSize: 12.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
            onClick={() => navigate('/login')}>
            <RiArrowLeftLine size={13} /> Back to sign in
          </span>
        </div>
      </div>
    </div>
  );
}
